import { useEffect, useId, useRef, useState, type DragEvent } from 'react';
import { FileArrowUp, ImageSquare, UploadSimple, X } from '@phosphor-icons/react';
import styles from './UploadBox.module.css';

export interface UploadBoxProps {
  label: string;
  hint: string;
  variant?: 'logo' | 'banner' | 'document' | 'file';
  accept?: string;
  maxSizeMB?: number;
  multiple?: boolean;
  maxFiles?: number;
  file?: File;
  previewUrl?: string | null;
  disabled?: boolean;
  busy?: boolean;
  onChange?: (file: File | undefined) => void;
  onFilesChange?: (files: File[]) => void;
  removable?: boolean;
}

export function UploadBox({ label, hint, variant = 'file', accept, maxSizeMB = 5, multiple = false, maxFiles = 8, file, previewUrl, disabled = false, busy = false, onChange, onFilesChange, removable = false }: UploadBoxProps) {
  const input = useRef<HTMLInputElement>(null);
  const id = useId();
  const [localUrl, setLocalUrl] = useState<string>();
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);
  const unavailable = disabled || busy;
  const image = variant !== 'file';
  const accepted = accept?.split(',').map(value => value.trim().toLowerCase()).filter(Boolean) ?? [];
  const formats = accepted.map(value => value === 'image/jpeg' ? 'JPG' : value.startsWith('.') ? value.toUpperCase() : value.replace('image/', '').toUpperCase()).join(', ');
  useEffect(() => {
    if (!file || !image) { setLocalUrl(undefined); return; }
    const url = URL.createObjectURL(file);
    setLocalUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file, image]);
  function choose(files: File[]) {
    if (unavailable || !files.length) return;
    setDragging(false);
    setError('');
    const limit = multiple ? maxFiles : 1;
    if (files.length > limit) { setError(`Choose up to ${limit} ${image ? 'image' : 'file'}${limit === 1 ? '' : 's'} at a time.`); return; }
    if (files.some(selected => accepted.length && !accepted.some(type => type.startsWith('.') ? selected.name.toLowerCase().endsWith(type) : type.endsWith('/*') ? selected.type.toLowerCase().startsWith(type.slice(0, -1)) : selected.type.toLowerCase() === type))) {
      setError(`Choose ${formats} ${image ? 'images' : 'files'}.`); return;
    }
    if (files.some(selected => !selected.size || selected.size > maxSizeMB * 1024 * 1024)) { setError(`Each file must not be empty and must be no larger than ${maxSizeMB} MB.`); return; }
    if (multiple) onFilesChange?.(files);
    else onChange?.(files[0]);
  }
  function drag(event: DragEvent<HTMLButtonElement>) {
    event.preventDefault();
    if (!unavailable) { event.dataTransfer.dropEffect = 'copy'; setDragging(true); }
  }
  const preview = previewUrl || localUrl;
  const selected = Boolean(file || preview);
  const kind = image ? 'image' : 'file';
  return <div className={styles.root} aria-busy={busy}>
    <span className={styles.label}>{label}</span>
    <input ref={input} className={styles.input} type="file" hidden aria-label={label} accept={accept} multiple={multiple} disabled={unavailable} onChange={event => { choose(Array.from(event.target.files ?? [])); event.target.value = ''; }} />
    <button type="button" className={styles.box} data-variant={variant} data-dragging={dragging && !unavailable} disabled={unavailable} aria-label={`${selected ? 'Change' : 'Choose'} ${label.toLowerCase()}`} aria-describedby={`${id}-hint${error ? ` ${id}-error` : ''}`} onClick={() => input.current?.click()} onDragEnter={drag} onDragOver={drag} onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false); }} onDrop={event => { event.preventDefault(); setDragging(false); choose(Array.from(event.dataTransfer.files)); }}>
      {image && preview ? <span className={styles.preview}><img src={preview} alt={`${label} preview`} /></span> : <span className={styles.icon}>{image ? <ImageSquare size={28} weight="duotone" /> : <FileArrowUp size={28} weight="duotone" />}</span>}
      <span className={styles.action}><UploadSimple size={17} />{busy ? 'Please wait…' : `${selected ? 'Change' : 'Choose'} ${kind}${multiple ? 's' : ''}`}</span>
      <span className={styles.dropHint}>{busy ? 'Preparing your upload' : 'or drag and drop here'}</span>
    </button>
    <p className={styles.hint} id={`${id}-hint`}>{hint}<span>{formats ? `${formats} · ` : ''}Up to {maxSizeMB} MB{multiple ? ' each' : ''}</span></p>
    {selected && <div className={styles.selection}><span title={file?.name}>{file?.name ?? 'Image selected'}{file && <small>{file.size < 1024 * 1024 ? `${Math.ceil(file.size / 1024)} KB` : `${(file.size / 1024 / 1024).toFixed(1)} MB`}</small>}</span>{removable && onChange && <button type="button" aria-label={`Remove ${label.toLowerCase()}`} disabled={unavailable} onClick={() => { setError(''); onChange(undefined); }}><X size={18} /></button>}</div>}
    {error && <p id={`${id}-error`} role="alert" className={styles.error}>{error}</p>}
  </div>;
}
